import cx from 'classnames';
import { Component } from 'react';
import { ChartMetaFormField, ContentItem } from 'models/ui';
import { ELSCheckBox, ELSIcon, ELSTagGroup } from 'components/common/els';

interface DisabilityAccommodationsDropdownProps {
  chartFormField: ChartMetaFormField;
  isDisabled?: boolean;
  ariaLabel?: string;
  placeholder?: string;
}

interface DisabilityAccommodationsDropdownState {
  isOpen: boolean;
}

class DisabilityAccommodationsDropdown extends Component<DisabilityAccommodationsDropdownProps, DisabilityAccommodationsDropdownState> {
  static displayName = 'DisabilityAccommodationsDropdown';

  private wrapperRef: HTMLDivElement | null = null;

  constructor(props: DisabilityAccommodationsDropdownProps) {
    super(props);
    this.state = { isOpen: false };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (event: MouseEvent) => {
    if (this.wrapperRef && !this.wrapperRef.contains(event.target as Node)) {
      this.setState({ isOpen: false });
    }
  };

  getVisibleOptions = (): ContentItem[] => this.props.chartFormField.chartContent?.filter((item) => !item.isHidden) ?? [];

  handleOptionClick = (option: ContentItem) => {
    if (option.onChange) option.onChange(this.props.chartFormField, option);
  };

  // A single onChange call with the fully-deselected field, rather than one onChange call per
  // selected option. Per-item onChange flows through the shared handleCheckBox handler, which reads
  // this.state.formFieldMap synchronously instead of via a setState updater - so calling it once per
  // option in a loop has each call recompute from the same stale snapshot, and only the last call's
  // change survives. Mirrors NestedMultiselectDropdown's batchUpdateSelections for the same reason.
  clearAllSelections = () => {
    const { chartFormField } = this.props;
    const newChartContent = (chartFormField.chartContent ?? []).map((item) => (item.selected ? { ...item, selected: false } : item));
    const desiredField = { ...chartFormField, chartContent: newChartContent, value: '' };
    if (chartFormField.onChange) chartFormField.onChange(desiredField, { target: { value: '' } });
  };

  getSelectedOptions = (): ContentItem[] => this.getVisibleOptions().filter((option) => option.selected);

  handleTagDelete = (index: number) => {
    const { chartFormField } = this.props;
    const changedOption = this.getSelectedOptions()[index];
    if (changedOption?.onChange) changedOption.onChange(chartFormField, changedOption);
  };

  render() {
    const { chartFormField, isDisabled, ariaLabel, placeholder } = this.props;
    const { isOpen } = this.state;
    const { name, disabled } = chartFormField;
    const shouldDisable = isDisabled || disabled;
    const options = this.getVisibleOptions();
    const selectedOptions = this.getSelectedOptions();

    return (
      <div
        className={cx('disability-multiselect', name)}
        ref={(ref) => {
          this.wrapperRef = ref;
        }}
      >
        <div className="disability-multiselect__control">
          <button
            type="button"
            className={cx('disability-multiselect__trigger', {
              'disability-multiselect__trigger--open': isOpen,
              'disability-multiselect__trigger--disabled': shouldDisable
            })}
            onClick={() => !shouldDisable && this.setState((prevState) => ({ isOpen: !prevState.isOpen }))}
            disabled={shouldDisable}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-label={ariaLabel}
            aria-controls={`${name}-listbox`}
          >
            <span className="disability-multiselect__placeholder">{placeholder || 'Select'}</span>
            <ELSIcon prefix="hmds" name="chevron-down" direction="right" size="1x1o2" />
          </button>

          {isOpen && (
            <div className="disability-multiselect__dropdown">
              <div className="disability-multiselect__actions">
                <button type="button" className="disability-multiselect__action-btn disability-multiselect__action-btn--reset" onClick={this.clearAllSelections}>
                  Reset
                </button>
                <button type="button" className="disability-multiselect__action-btn disability-multiselect__action-btn--done" onClick={() => this.setState({ isOpen: false })}>
                  Done
                </button>
              </div>
              <div id={`${name}-listbox`} role="listbox" className="disability-multiselect__options">
                {options.map((option) => (
                  <div key={option.id} className="disability-multiselect__option">
                    <ELSCheckBox
                      id={`${name}-option-${option.id}`}
                      checked={!!option.selected}
                      isDisabled={option.disabled}
                      labelText={option.label}
                      changeHandler={() => this.handleOptionClick(option)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {!!selectedOptions.length && (
          <div className="u-els-margin-top">
            <ELSTagGroup
              isLargerChipText
              isTagDeletable={!shouldDisable}
              cssModifier="disability-multiselect__tag-name-group"
              tags={selectedOptions.map((option) => option.label || option.name || option.value)}
              onDeleteTag={this.handleTagDelete}
            />
          </div>
        )}
      </div>
    );
  }
}

export default DisabilityAccommodationsDropdown;
